import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { setAnswerValue } from "@formsort/custom-question-api";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { tv } from "tailwind-variants";

import { emailCheckAnswerTypeSchema, emailCheckFormSchema } from "@/domains/profile/models/schemas";
import type { EmailCheckForm } from "@/domains/profile/models/types";
import { useVerifyEmail } from "@/domains/profile/mutations/use-verify-email";
import { useCustomQuestionAutoheight } from "@/routes/_guest/profiling/_custom-questions/-hooks/use-custom-question-autoheight";
import { useFlowAnswer } from "@/routes/_guest/profiling/_custom-questions/-hooks/use-flow-answer";
import { Heading, Input } from "@/ui";
import { validateError } from "@/utils/validation";

const emailCheckVariants = tv({
  slots: {
    emailField: "flex flex-col",
  },
  variants: {
    invalid: {
      true: {
        emailField: "-ml-2.5 gap-2 border-l-2 border-danger pl-2.5",
      },
    },
  },
});

const { emailField } = emailCheckVariants();

const EmailCheck = () => {
  const previousEmailAnswer = useFlowAnswer("email_check", emailCheckAnswerTypeSchema)?.address;

  const {
    formState: { errors, isValid },
    register,
    setError,
    watch,
  } = useForm<EmailCheckForm>({
    mode: "onChange",
    resolver: zodResolver(emailCheckFormSchema),
    values: {
      email: previousEmailAnswer ?? "",
    },
  });

  const { containerRef } = useCustomQuestionAutoheight();

  const { isPending: isVerifyingEmail, mutate: verifyEmail } = useVerifyEmail();

  const currentEmail = watch("email");

  useEffect(() => {
    if (!isValid) {
      return;
    }

    const timeoutId = setTimeout(() => {
      verifyEmail(
        { email: currentEmail },
        {
          onSuccess: ({ HP }) => {
            setAnswerValue({
              address: currentEmail,
              HP,
            });
          },
          onError: (err) => {
            const error = validateError(err);
            setError("email", {
              message:
                error?.response.data.error.message ||
                "An error occurred while verifying the email. Please try again.",
            });
          },
        },
      );
    }, 500);

    return () => {
      return clearTimeout(timeoutId);
    };
  }, [isValid, verifyEmail, currentEmail, setError]);

  return (
    <div className="flex flex-col gap-8" ref={containerRef}>
      <Heading className="text-2xl sm:text-2xl" level={2}>
        Enter your email address.
      </Heading>
      <div className={emailField({ invalid: !!errors.email })}>
        <Input
          aria-label="Email address"
          autoComplete="email"
          className="h-15 rounded-lg bg-surface p-2 text-lg font-medium placeholder:font-semibold"
          disabled={isVerifyingEmail}
          inputMode="email"
          placeholder="Type your email address here."
          type="email"
          {...register("email")}
        />
        {errors.email ? <p className="font-semibold text-danger">{errors.email.message}</p> : null}
      </div>
    </div>
  );
};

export const Route = createFileRoute("/_guest/profiling/_custom-questions/email-check")({
  component: EmailCheck,
});
